import { Injectable, Signal, signal } from '@angular/core';

type ToastType = 'error' | 'info' | 'success' | 'warning';

const toastIcons: Record<ToastType, string> = {
  error: 'x-circle',
  info: 'info',
  success: 'check-circle',
  warning: 'alert-triangle',
};

export interface ToastAction {
  handler: () => void;
  label: string;
}

export interface Toast {
  action?: ToastAction;
  duration: number;
  icon: string;
  id: string;
  message: string;
  status: 'show' | 'hide';
  title: string;
  type: ToastType;
}

const defaultDuration = 8000;

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private autoHideTimeout: ReturnType<typeof setTimeout> | null= null;
  private queue = signal<Toast[]>([]);
  private currentToast = signal<Toast | null>(null);
  private isProcessing = false;

  getQueue(): Signal<Toast[]> {
    return this.queue.asReadonly();
  }

  getCurrentToast(): Signal<Toast | null> {
    return this.currentToast.asReadonly();
  }

  hideCurrentToast(): void {
    if (!this.currentToast() || this.currentToast()?.status === 'hide') {
      return;
    }

    if (this.autoHideTimeout) {
      clearTimeout(this.autoHideTimeout);
      this.autoHideTimeout = null;
    }

    this.hideAndCleanup();
  }

  showInfo(title: string, message: string, duration = defaultDuration): void {
    this.addToast('info', title, message, duration);
  }

  showSuccess(title: string, message: string, duration = defaultDuration): void {
    this.addToast('success', title, message, duration);
  }

  showWarning(title: string, message: string, duration = defaultDuration): void {
    this.addToast('warning', title, message, duration);
  }

  showError(title: string, message: string, action?: ToastAction, duration = defaultDuration): void {
    this.addToast('error', title, message, duration, action);
  }

  private addToast(type: ToastType, title: string, message: string, duration: number, action?: ToastAction): void {
    const toast: Toast = {
      action,
      duration,
      icon: toastIcons[type],
      id: this.generateId(),
      message,
      status: 'show',
      title,
      type,
    };
    this.queue.update((queue) => [...queue, toast]);
    this.processQueue();
  }

  private hideAndCleanup(): void {
    this.currentToast.update((toast) => ({
      ...toast!,
      status: 'hide',
    }));
    setTimeout(() => {
      this.currentToast.set(null);
      this.autoHideTimeout = null;
      this.isProcessing = false;
      this.processQueue();
    }, 300);
  }

  private processQueue(): void {
    if (this.isProcessing || this.queue().length === 0) {
      return;
    }

    this.isProcessing = true;
    const nextToast = this.queue()[0];
    this.currentToast.set(nextToast);
    this.queue.update((queue) => queue.slice(1));
    this.autoHideTimeout = setTimeout(() => this.hideAndCleanup(), nextToast.duration);
  }

  private generateId(): string {
    return Math.random().toString(36).substr(2, 9);
  }
}
